import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * <sticky-dynamic-checkout-n>
 *
 * Custom-template-only controller for the sticky bar CTA on
 * `product.customproduct`. It wraps the shared `<sticky-add-to-cart>` element
 * (whose image / title / price / scroll-visibility behaviour is left untouched)
 * and re-points the sticky button's click at the REAL Shopify dynamic-checkout
 * button rendered by the main Product Information CTA:
 *
 *   .custom-product-information-n .product-cta-n .shopify-payment-button__button--unbranded
 *
 * i.e. Shopify's native Buy-It-Now / direct-checkout action for THIS section's
 * product. It never adds to cart, never builds a checkout URL, never calls a
 * cart API, and never clones or moves Shopify's payment button — it only clicks
 * the existing one. The sticky button is kept disabled until that real target
 * has been injected (Shopify renders it asynchronously) and is usable.
 *
 * @extends {Component}
 */
class StickyDynamicCheckoutN extends Component {
  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {MutationObserver | null} */
  #paymentObserver = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#button = this.querySelector('.sticky-add-to-cart__button');
    this.#syncState();

    // Shopify injects the dynamic-checkout button asynchronously. Observe ONLY
    // the main CTA's payment-button wrapper (never the document) so we can
    // enable the sticky button once the real target appears, and mirror its
    // disabled/availability changes.
    const wrapper = this.#paymentWrapper();
    if (wrapper) {
      this.#paymentObserver = new MutationObserver(() => this.#syncState());
      this.#paymentObserver.observe(wrapper, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'hidden'],
      });
    }

    // Sticky quantity remote: keep the sticky −/N/+ display and its disabled
    // states mirrored to the canonical main quantity selector. Both the main
    // selector's own changes AND the sticky-triggered updateQuantity() dispatch
    // ThemeEvents.quantitySelectorUpdate, so this one listener syncs both
    // directions. pageshow covers the bfcache restore (returning from Checkout),
    // where the input is restored but no quantity event fires.
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    this.#syncQty();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#paymentObserver?.disconnect();
    this.#paymentObserver = null;
    this.#abortController.abort();
  }

  /**
   * Declarative handler for `on:click="sticky-dynamic-checkout-n/handleCheckout"`.
   * Triggers Shopify's real dynamic-checkout button (skips cart → Checkout).
   * @param {Event} [event]
   */
  handleCheckout = (event) => {
    event?.preventDefault?.();
    const target = this.#target();
    if (!target || this.#isDisabled(target)) return;
    target.click();
  };

  /**
   * Sticky − / + delegate to the canonical main quantity selector's public
   * updateQuantity(), so Horizon's min/max/step validation runs and the single
   * QuantitySelectorUpdateEvent it dispatches syncs the main selector, both
   * totals, the CTA label and this display. No separate quantity/form state.
   * @param {Event} [event]
   */
  handleQtyDecrease = (event) => {
    event?.preventDefault?.();
    this.#mainQty()?.updateQuantity?.(-1);
  };

  /** @param {Event} [event] */
  handleQtyIncrease = (event) => {
    event?.preventDefault?.();
    this.#mainQty()?.updateQuantity?.(1);
  };

  /** @param {Event} event */
  #onQuantityUpdate = (event) => {
    const detail = /** @type {{ cartLine?: number }} */ (/** @type {CustomEvent} */ (event).detail);
    if (detail?.cartLine != null) return; // ignore cart-drawer line quantity changes
    const section = this.closest('.shopify-section');
    if (!section || !(event.target instanceof Node) || !section.contains(event.target)) return;
    this.#syncQty();
  };

  #onPageShow = () => {
    // A history restore (bfcache OR the browser restoring the input value on a
    // normal Back navigation) puts the quantity back into the input but does NOT
    // re-run the quantity selector's button-state logic — so the minus button can
    // stay disabled (state from quantity 1) while the value shows e.g. 3.
    // Recalculate the canonical main selector's states on the next frame (so the
    // restored value is in place), then mirror them onto the sticky remote. This
    // enables/disables BOTH minus buttons per the restored quantity vs. minimum.
    // updateButtonStates() respects the component's server-disabled flags, so
    // product availability handling is preserved.
    requestAnimationFrame(() => {
      this.#mainQty()?.updateButtonStates?.();
      this.#syncQty();
    });
  };

  /**
   * The canonical main quantity selector in this section (single source of truth).
   * @returns {any}
   */
  #mainQty() {
    return (
      this.closest('.shopify-section')?.querySelector(
        'product-form-component quantity-selector-component'
      ) ?? null
    );
  }

  /** Reflect the main quantity value + min/max disabled states onto the sticky remote. */
  #syncQty() {
    const qs = this.#mainQty();
    const value = this.querySelector('.sticky-qty-n__value');
    if (!qs || !value) return;
    const input = /** @type {HTMLInputElement | null} */ (qs.querySelector('input[name="quantity"]'));
    if (input) value.textContent = String(input.value);
    const minus = /** @type {HTMLButtonElement | null} */ (this.querySelector('.sticky-qty-n__minus'));
    const plus = /** @type {HTMLButtonElement | null} */ (this.querySelector('.sticky-qty-n__plus'));
    const mainMinus = /** @type {HTMLButtonElement | null} */ (qs.querySelector('[name="minus"]'));
    const mainPlus = /** @type {HTMLButtonElement | null} */ (qs.querySelector('[name="plus"]'));
    if (minus && mainMinus) minus.disabled = mainMinus.disabled;
    if (plus && mainPlus) plus.disabled = mainPlus.disabled;
  }

  /**
   * The active Product Information root inside this component's own section.
   * @returns {Element | null}
   */
  #root() {
    const section = this.closest('.shopify-section');
    return section?.querySelector('.custom-product-information-n') ?? null;
  }

  /**
   * The main CTA's payment-button wrapper (server-rendered container that
   * Shopify populates with the dynamic-checkout button).
   * @returns {Element | null}
   */
  #paymentWrapper() {
    return (
      this.#root()?.querySelector(
        '.product-cta-n .shopify-payment-button, .product-cta-n .accelerated-checkout-block'
      ) ?? null
    );
  }

  /**
   * Shopify's unbranded dynamic-checkout button for this product (never a
   * branded Shop Pay / Apple Pay / Google Pay / PayPal wallet button).
   * @returns {HTMLButtonElement | null}
   */
  #target() {
    const el = this.#root()?.querySelector(
      '.product-cta-n .shopify-payment-button__button--unbranded'
    );
    return /** @type {HTMLButtonElement | null} */ (el ?? null);
  }

  /**
   * @param {Element} el
   * @returns {boolean}
   */
  #isDisabled(el) {
    return (
      /** @type {HTMLButtonElement} */ (el).disabled === true ||
      el.getAttribute('aria-disabled') === 'true' ||
      el.closest('.accelerated-checkout-block[hidden]') != null
    );
  }

  /**
   * Keeps the sticky button disabled until the real dynamic-checkout action is
   * present and usable, so it never falls back to Add to Cart or a dead click.
   */
  #syncState() {
    if (!this.#button) return;
    const target = this.#target();
    const ready = Boolean(target) && !this.#isDisabled(/** @type {Element} */ (target));
    this.#button.disabled = !ready;
  }
}

if (!customElements.get('sticky-dynamic-checkout-n')) {
  customElements.define('sticky-dynamic-checkout-n', StickyDynamicCheckoutN);
}
